import { Fetcher } from '@this/src/util';
/* eslint max-lines: 0 */
import React, { useEffect, useState, useMemo } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';

import HotelList from '@this/domain/hotel/hotel_list';
import type FlightList from '@this/domain/flight/flight_list';
import type TransitList from '@this/domain/transit/transit_list';

import User from '@this/domain/user/user';
import type UserJson from '@this/domain/user/user_json';
import SelectListAreaSearchTemplate from './list_area_search/list_area_search.template';
import type { SelectListAreaProps, SelectListAreaState } from './list_area';
import HotelListAreaContent from './HotelListAreaContent';
import TransportListAreaContent from './TransportListAreaContent';

type Props = SelectListAreaProps &
  SelectListAreaState & {
    // SelectListArea.methods
    handleTransportSegmentClick: (i: number) => () => void;
    toggleShowSearchBox: () => void;
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    direction?: string;
    handleFlightConditionSubmit: (
      filteredUpgradeSeat: boolean,
      carrierIds: string[],
      changeablePrices: string[],
      showConnectingAir: boolean
    ) => void;
    openFlightCondition: () => void;
    closeFlightCondition: () => void;
    isOpenFlightCondition: boolean;
  };
const ListAreaLayout: React.FC<Props> = ({
  query,
  store,
  repository,
  serviceId,

  icon,
  listWrapper,
  type,
  subtype,
  show,
  queryItem,
  resultItem,
  direction,

  transportSegment,
  errors,
  showSearchBox,

  handleTransportSegmentClick,
  toggleShowSearchBox,
  handleSubmit,
  handleFlightConditionSubmit,
  openFlightCondition,
  closeFlightCondition,
  isOpenFlightCondition
}) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme, show), [theme, show]);

  //　MEMO: 根っこでspとpc分けてるがここでも分岐？
  const isMobile = useMediaQuery('(max-width:768px)');

  // NOTE: このFetcher.getは本来不要
  // 実装PR https://github.com/BEST10developers/travel.ai/pull/7689
  //
  // 問題: store.user.organization.enable_multiple_hotel_plansにアクセスしたいが、
  //       SelectStore.tsの型定義が未完成（// TODO: UserJson定義）で、
  //       organizationに3プロパティしか定義されていないためTypeScriptエラーになる
  //
  // 実態: store.userは実行時には完全なorganizationデータを持っている
  //       （CoreAppで/users.jsonから取得したUserインスタンスがそのまま渡されている）
  //
  // 回避策として同じ/users.jsonを再取得してUser型のmeに格納している
  //
  // 根本的な解決策: SelectStore.tsのuser型をUser | nullに変更すれば、
  //                このFetcher.getは削除可能
  const [me, setMe] = useState<User | null>(null);
  useEffect(() => {
    const abortController = new AbortController();

    Fetcher.get<UserJson>('/users.json', undefined, undefined, abortController.signal).then(
      response => {
        setMe(new User(response));
      },
      error => {
        if (error.name !== 'AbortError') {
          setMe(null);
        }
      }
    );

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <div style={styles.listArea} className="select-list-area">
      {store.result.type === 'separate' &&
        (store.result.isRoundTripSearchResult() || store.result.isMultipleLocationsSearchResult()) &&
        store.result.items.length > 0 &&
        query &&
        queryItem &&
        // 海外旅券は往復で1セットなことが多いため、往路のみ、復路のみで再検索できないようにする
        type !== 'flight' && (
          <SelectListAreaSearchTemplate
            showSearchBox={showSearchBox}
            toggleShowSearchBox={toggleShowSearchBox}
            handleSubmit={handleSubmit}
            queryItem={queryItem}
            query={query}
            errors={errors}
          />
        )}

      {resultItem && resultItem.loading ? (
        <>
          <img className="select__loading" src="/images/loading.gif" width={50} height={50} alt="" />
          {type === 'transport' ? (
            <div className="select__loading-text">経路を検索中です</div>
          ) : type === 'flight' ? (
            <div className="select__loading-text">航空券を検索中です</div>
          ) : type === 'hotel' ? (
            <div className="select__loading-text">ホテルを検索中です</div>
          ) : null}
        </>
      ) : !listWrapper ? null : (
        <>
          {type === 'hotel' && listWrapper instanceof HotelList && (
            <HotelListAreaContent
              listWrapper={listWrapper}
              resultItem={resultItem}
              icon={icon}
              serviceId={serviceId}
              store={store}
              isMobile={isMobile}
              me={me}
              repository={repository}
            />
          )}
          {(type === 'transport' || type === 'flight') && (
            <TransportListAreaContent
              type={type}
              listWrapper={listWrapper as TransitList | FlightList}
              icon={icon}
              subtype={subtype}
              resultItem={resultItem}
              store={store}
              repository={repository}
              direction={direction}
              queryItem={queryItem}
              transportSegment={transportSegment}
              handleTransportSegmentClick={handleTransportSegmentClick}
              openFlightCondition={openFlightCondition}
              closeFlightCondition={closeFlightCondition}
              isOpenFlightCondition={isOpenFlightCondition}
              handleFlightConditionSubmit={handleFlightConditionSubmit}
            />
          )}
        </>
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme, show: boolean) => {
  const isEditPage = location.pathname.match(/trips\/\d+\/edit/);
  const backgroundColor = isEditPage ? '#fff' : theme.accentColorLight;

  return {
    listArea: {
      flexGrow: 99999,
      background: backgroundColor,
      padding: '10px',
      position: 'relative' as const,
      display: show ? 'block' : ('none' as const)
    }
  };
};

export default ListAreaLayout;
